import JSZip from 'jszip';

/**
 * Build a downloadable .zip from the in-memory working tree and trigger a
 * browser download. The zip is structured so it opens and runs cleanly in real
 * VS Code: nested folders are reconstructed from the flat `{path: contents}`
 * map, node_modules is excluded, and package.json / tsconfig / .gitignore are
 * injected if the project doesn't already provide them.
 */
export async function downloadProjectZip(
  projectName: string,
  fileContents: { [path: string]: string },
): Promise<void> {
  const zip = new JSZip();

  const hasFile = (name: string) =>
    Object.prototype.hasOwnProperty.call(fileContents, `/${name}`) ||
    Object.prototype.hasOwnProperty.call(fileContents, name);

  for (const [rawPath, content] of Object.entries(fileContents)) {
    const path = rawPath.replace(/^\/+/, '');
    if (!path) continue;
    // Never ship installed dependencies or build output.
    if (path === 'node_modules' || path.startsWith('node_modules/') || path.includes('/node_modules/')) continue;
    if (path === 'dist' || path.startsWith('dist/')) continue;
    zip.file(path, content); // JSZip creates intermediate folders from the path
  }

  // Inject sensible defaults only if absent, so the unzip → install → run flow works.
  if (!hasFile('.gitignore')) {
    zip.file('.gitignore', 'node_modules/\ndist/\n.env\n');
  }
  // tsconfig.json must always ship so the exported project type-checks/runs (Req 6.3).
  if (!hasFile('tsconfig.json')) {
    zip.file(
      'tsconfig.json',
      JSON.stringify(
        {
          compilerOptions: {
            target: 'ES2020',
            module: 'ESNext',
            moduleResolution: 'node',
            outDir: './dist',
            rootDir: './src',
            strict: true,
            esModuleInterop: true,
            skipLibCheck: true,
            forceConsistentCasingInFileNames: true,
            allowSyntheticDefaultImports: true,
          },
          include: ['src/**/*'],
          exclude: ['node_modules', 'dist'],
        },
        null,
        2,
      ),
    );
  }
  if (!hasFile('package.json')) {
    zip.file(
      'package.json',
      JSON.stringify(
        {
          name: safeName(projectName),
          version: '1.0.0',
          type: 'module',
          scripts: {
            dev: 'tsx src/app.ts',
            build: 'tsc',
            start: 'npm run build && node dist/app.js',
          },
          dependencies: {},
          devDependencies: {
            '@types/node': '^20.0.0',
            tsx: '^4.0.0',
            typescript: '^5.0.0',
          },
        },
        null,
        2,
      ),
    );
  }

  const blob = await zip.generateAsync({ type: 'blob' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${safeName(projectName)}.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function safeName(name: string): string {
  return (
    (name || 'project')
      .toLowerCase()
      .replace(/[^a-z0-9-_]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'project'
  );
}
